import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabase';
import Logo from '../components/common/Logo';
import { toast } from 'react-hot-toast';
import signup from '../assets/sign_up.avif'

function SignUp() {
  const location = useLocation();
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    currentCity: '',
    //userType: 'teacher' Default to teacher
    userType: location.state?.userType || 'teacher'
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showOTPInput, setShowOTPInput] = useState(false);
  const [otpValues, setOtpValues] = useState(['', '', '', '', '', '']);
  const [resendTimer, setResendTimer] = useState(0);
  const [canResend, setCanResend] = useState(false);
  const otpRefs = [
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null)
  ];
  const navigate = useNavigate();
  const { signUp } = useAuth();

  useEffect(() => {
    let interval;
    if (resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer((prev) => prev - 1);
      }, 1000);
    } else {
      setCanResend(true);
    }
    return () => clearInterval(interval);
  }, [resendTimer]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleOtpChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;

    const newOtpValues = [...otpValues];
    newOtpValues[index] = value;
    setOtpValues(newOtpValues);

    if (value !== '' && index < 5) {
      otpRefs[index + 1].current.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace') {
      if (otpValues[index] === '' && index > 0) {
        otpRefs[index - 1].current.focus();
      }
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').slice(0, 6);
    const digits = pastedData.replace(/\D/g, '').split('');
    
    const newOtpValues = [...otpValues];
    digits.forEach((digit, index) => {
      if (index < 6) {
        newOtpValues[index] = digit;
      }
    });
    setOtpValues(newOtpValues);

    const nextEmptyIndex = newOtpValues.findIndex(val => val === '');
    if (nextEmptyIndex !== -1) {
      otpRefs[nextEmptyIndex].current.focus();
    } else if (digits.length > 0) {
      otpRefs[5].current.focus();
    }
  };

  const startResendTimer = () => {
    setResendTimer(60);
    setCanResend(false);
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    try {
      setError('');
      setLoading(true);

      // Check if user already exists
      const { data: existingUsers } = await supabase
        .from('profiles')
        .select('id')
        .eq('email', formData.email);

      if (existingUsers?.length > 0) {
        throw new Error('User with this email already exists');
      }

      // Sign up with Supabase auth
      const { data, error: signUpError } = await supabase.auth.signUp({
        email: formData.email,
        password: formData.password,
        options: {
          emailRedirectTo: `${window.location.origin}/login`
        }
      });

      if (signUpError) throw signUpError;

      if (data) {
        // Store OTP attempt
        const { error: otpError } = await supabase
          .from('auth_otp_attempts')
          .insert([
            {
              user_email: formData.email,
              expires_at: new Date(Date.now() + 5 * 60000).toISOString() // 5 minutes expiry
            }
          ]);

        if (otpError) throw otpError;

        toast.success('Please check your email for the verification code');
        setShowOTPInput(true);
        startResendTimer();
      }
    } catch (error) {
      setError(error.message);
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    if (!canResend) return;

    try {
      setLoading(true);
      setError('');

      // Invalidate old OTP by updating expires_at
      await supabase
        .from('auth_otp_attempts')
        .update({ expires_at: new Date().toISOString() })
        .eq('user_email', formData.email);

      // Request new OTP
      const { error: signUpError } = await supabase.auth.signUp({
        email: formData.email,
        password: formData.password,
        options: {
          emailRedirectTo: `${window.location.origin}/login`
        }
      });

      if (signUpError) throw signUpError;

      // Store new OTP attempt
      const { error: otpError } = await supabase
        .from('auth_otp_attempts')
        .insert([
          {
            user_email: formData.email,
            expires_at: new Date(Date.now() + 5 * 60000).toISOString() // 5 minutes expiry
          }
        ]);

      if (otpError) throw otpError;

      toast.success('New verification code sent to your email');
      startResendTimer();
      setOtpValues(['', '', '', '', '', '']);
      otpRefs[0].current.focus();
    } catch (error) {
      setError(error.message);
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const verifyOTP = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError('');

      const otp = otpValues.join('');
      if (otp.length !== 6) {
        throw new Error('Please enter complete OTP');
      }

      try {
        // Check if OTP is expired
        const { data: otpAttempt } = await supabase
          .from('auth_otp_attempts')
          .select('expires_at')
          .eq('user_email', formData.email)
          .order('created_at', { ascending: false })
          .limit(1)
          .single();

        if (otpAttempt && new Date(otpAttempt.expires_at) < new Date()) {
          throw new Error('OTP has expired. Please request a new one.');
        }
      } catch (otpError) {
        console.log('OTP attempt check skipped:', otpError);
      }

      const { data, error: verifyError } = await supabase.auth.verifyOtp({
        email: formData.email,
        token: otp,
        type: 'signup'
      });

      if (verifyError) throw verifyError;

      if (data?.user) {
        // Create profile after successful verification
        const { error: profileError } = await supabase
          .from('profiles')
          .insert([
            {
              id: data.user.id,
              email: formData.email,
              first_name: formData.firstName,
              last_name: formData.lastName,
              current_city: formData.currentCity,
              user_type: formData.userType
            }
          ]);

        if (profileError) throw profileError;

        toast.success('Account created successfully!');
        navigate('/personal-information'); // Changed to redirect to landing page
      }
    } catch (error) {
      setError(error.message);
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Image */}
      <div className="hidden lg:block lg:w-[55%]">
        <img 
          src={signup}
          alt="Teachers collaborating" 
          className="w-full h-screen object-cover object-center"
        />
      </div>

      {/* Right side - Sign Up Form */}
      <div className="w-full lg:w-[45%] flex items-center justify-center bg-primary-extra px-6">
        <div className="w-full max-w-[440px]">
          {/* Logo */}
          <div className="flex items-center justify-center mb-12">
            <Logo />
          </div>

          <h1 className="text-3xl font-bold text-primary-500 mb-8 text-center">SIGN UP</h1>
          
          {error && (
            <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
              {error}
            </div>
          )}

          {!showOTPInput ? (
            <form onSubmit={handleSignUp} className="space-y-6">
              {/* User Type Selection */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-primary-text mb-2">
                  For <span className="text-red-500">*</span>
                </label>
                <div className="flex gap-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="userType"
                      value="teacher"
                      checked={formData.userType === 'teacher'}
                      onChange={(e) => setFormData(prev => ({ ...prev, userType: e.target.value }))}
                      className="form-radio text-primary-500 focus:ring-primary-500"
                      required
                    />
                    <span className="ml-2 text-primary-text">Teacher</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="userType"
                      value="institution"
                      checked={formData.userType === 'institution'}
                      onChange={(e) => setFormData(prev => ({ ...prev, userType: e.target.value }))}
                      className="form-radio text-primary-500 focus:ring-primary-500"
                      required
                    />
                    <span className="ml-2 text-primary-text">Institution</span>
                  </label>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    placeholder="First Name"
                    className="w-full px-4 py-3.5 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    required
                  />
                </div>
                <div>
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    placeholder="Last Name"
                    className="w-full px-4 py-3.5 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    required
                  />
                </div>
              </div>

              <div>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="Email Address"
                  className="w-full px-4 py-3.5 rounded-lg border text-primary-text border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  required
                />
              </div>

              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  placeholder="Create Password"
                  className="w-full px-4 py-3.5 rounded-lg border text-primary-text border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-primary-text hover:text-gray-700"
                >
                  {showPassword ? (
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
                    </svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  )}
                </button>
              </div>

              <div>
                <input
                  type="text"
                  name="currentCity"
                  value={formData.currentCity}
                  onChange={handleInputChange}
                  placeholder="Current City"
                  className="w-full px-4 py-3.5 rounded-lg border text-primary-text border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  required
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-primary-500 text-primary-50 py-3.5 rounded-lg hover:bg-opacity-90 transition-colors duration-200 disabled:opacity-50 font-medium"
              >
                {loading ? 'Creating Account...' : 'Sign Up'}
              </button>
            </form>
          ) : (
            <form onSubmit={verifyOTP} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-4">
                  Enter Verification Code
                </label>
                <div 
                  className="flex justify-between gap-2"
                  onPaste={handlePaste}
                >
                  {otpValues.map((value, index) => (
                    <input
                      key={index}
                      type="text"
                      ref={otpRefs[index]}
                      value={value}
                      onChange={(e) => handleOtpChange(index, e.target.value)}
                      onKeyDown={(e) => handleKeyDown(index, e)}
                      maxLength={1}
                      className="w-12 h-12 text-center text-xl text-primary-text font-semibold rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      required
                    />
                  ))}
                </div>
                <div className="mt-4 text-center">
                  {resendTimer > 0 ? (
                    <p className="text-gray-500">
                      Resend OTP in {resendTimer} seconds
                    </p>
                  ) : (
                    <button
                      type="button"
                      onClick={handleResendOTP}
                      disabled={!canResend || loading}
                      className="text-primary-500 hover:text-primary-600 font-medium disabled:opacity-50"
                    >
                      Resend OTP
                    </button>
                  )}
                </div>
              </div>

              <button
                type="submit"
                disabled={loading || otpValues.some(v => v === '')}
                className="w-full bg-primary-500 text-white py-3.5 rounded-lg hover:bg-opacity-90 transition-colors duration-200 disabled:opacity-50 font-medium"
              >
                {loading ? 'Verifying...' : 'Verify OTP'}
              </button>
            </form>
          )}

          <p className="mt-8 text-center text-primary-text">
            Already have an account?{' '}
            <Link to="/login" className="text-primary-400 hover:underline font-semibold">
              Login
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}

export default SignUp;